import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/motion";
import { SectionLabel, CtaSection } from "../components/common";
import { NEWS } from "../data/site";

export default function NewsArticle() {
  const { slug } = useParams();
  const article = NEWS.find((n) => n.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!article) return <Navigate to="/news" replace />;

  const more = NEWS.filter((n) => n.slug !== slug).slice(0, 3);

  return (
    <>
      <Seo
        title={`${article.title} | STERILTRON News`}
        description={article.excerpt}
        path={`/news/${article.slug}`}
      />

      {/* HERO */}
      <section className="relative pt-28 md:pt-36 bg-brand-950 text-white overflow-hidden">
        <div className="absolute inset-0 bp-grid-dark opacity-50" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 pt-10 pb-16 md:pb-20">
          <nav className="flex items-center gap-2 text-xs text-brand-300 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link><ChevronRight size={12} />
            <Link to="/news" className="hover:text-white">News</Link><ChevronRight size={12} />
            <span className="text-brand-100">{article.category}</span>
          </nav>
          <SectionLabel dark>{article.category}</SectionLabel>
          <h1 className="mt-6 font-heading text-3xl md:text-5xl font-extrabold tracking-tighter leading-[1.02] text-balance">{article.title}</h1>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-brand-200">
            <span>{article.author}</span>
            <span className="text-brand-500">•</span>
            <span>{article.date}</span>
            <span className="text-brand-500">•</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {article.readTime}</span>
          </div>
        </div>
      </section>

      {/* FEATURED IMAGE */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 -mt-8 md:-mt-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden border border-slate-200 bg-white"
        >
          <img src={article.image} alt={article.title} className="w-full h-[300px] md:h-[480px] object-cover" />
        </motion.div>
      </div>

      {/* BODY */}
      <article className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="font-heading text-xl md:text-2xl font-medium tracking-tight text-brand-950 leading-snug text-balance">{article.excerpt}</p>
          <div className="mt-8 space-y-6">
            {article.body.map((p, i) => (
              <Reveal key={i} delay={i * 0.03}>
                <p className="text-slate-700 text-base md:text-lg leading-relaxed">{p}</p>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-slate-200">
            <Link to="/news" data-testid="news-back" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-brand-800 hover:gap-3 transition-all">
              <ArrowLeft size={16} /> Back to News
            </Link>
          </div>
        </div>
      </article>

      {/* MORE */}
      <section className="py-16 md:py-24 bg-brand-50/40 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionLabel>More from STERILTRON</SectionLabel>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {more.map((n) => (
              <Link key={n.slug} to={`/news/${n.slug}`} data-testid={`more-news-${n.slug}`} className="group block bg-white border border-slate-200 hover:border-brand-800 transition-colors">
                <div className="overflow-hidden">
                  <img src={n.image} alt={n.title} loading="lazy" className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-widest">
                    <span className="text-brand-700 font-semibold">{n.category}</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-slate-500">{n.date}</span>
                  </div>
                  <h3 className="mt-3 font-heading text-base font-semibold tracking-tight text-brand-950 leading-snug">{n.title}</h3>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-800">Read <ArrowUpRight size={13} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
